import { usePathname } from 'expo-router';
import { useCallback } from 'react';
import { segmentClient } from 'services/analytics/segment/segment';
import { VaroCategories } from 'shared/analytics/types/categories';
import {
  ButtonPressedProperties,
  ComponentEventNames,
  ComponentViewedProperties,
  ListItemPressedProperties,
  ScreenViewedProperties,
} from 'shared/analytics/types/componentEvents';
import { enrich } from 'shared/analytics/utils/enrich';

// Resolved once so hook order stays stable: tests routinely mock expo-router
// without usePathname, and a missing source must not break rendering.
const useSourceName: () => string | undefined =
  typeof usePathname === 'function' ? usePathname : () => undefined;

export const useComponentAnalytics = () => {
  const sourceName = useSourceName();

  const logPressableClicked = useCallback(
    <TCategory extends VaroCategories>(
      properties: ButtonPressedProperties<TCategory>,
    ) => {
      segmentClient.logEvent(
        ComponentEventNames.BUTTON_PRESSED,
        enrich({ ...properties, source_name: sourceName }),
      );
    },
    [sourceName],
  );

  const logListItemPressed = useCallback(
    <TCategory extends VaroCategories>(
      properties: ListItemPressedProperties<TCategory>,
    ) => {
      segmentClient.logEvent(
        ComponentEventNames.LIST_ITEM_PRESSED,
        enrich({ ...properties, source_name: sourceName }),
      );
    },
    [sourceName],
  );

  const logScreenViewed = useCallback(
    <TCategory extends VaroCategories>(
      properties: ScreenViewedProperties<TCategory>,
    ) => {
      segmentClient.logEvent(
        ComponentEventNames.SCREEN_VIEWED,
        enrich({ ...properties, source_name: sourceName }),
      );
    },
    [sourceName],
  );

  const logComponentViewed = useCallback(
    <TCategory extends VaroCategories>(
      properties: ComponentViewedProperties<TCategory>,
    ) => {
      segmentClient.logEvent(
        ComponentEventNames.COMPONENT_VIEWED,
        enrich({ ...properties, source_name: sourceName }),
      );
    },
    [sourceName],
  );

  return {
    logPressableClicked,
    logListItemPressed,
    logScreenViewed,
    logComponentViewed,
  };
};
